import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  LinearProgress,
  Button,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { User } from '../../types/auth';
import { useNavigate } from 'react-router-dom';

/**
 * WelcomeCard Component
 *
 * Personalized welcome banner for dashboard
 *
 * Features:
 * - User avatar and name
 * - Role badge
 * - Profile completeness indicator
 * - Quick action to complete profile
 *
 * @component
 * @param {WelcomeCardProps} props - Component props
 * @returns {JSX.Element} Rendered welcome card
 */
interface WelcomeCardProps {
  user: User;
}

export const WelcomeCard: React.FC<WelcomeCardProps> = ({ user }) => {
  const navigate = useNavigate();

  // ===== Helper Functions =====
  const getRoleColor = (role: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'editor':
        return 'warning';
      case 'contributor':
        return 'info';
      default:
        return 'default';
    }
  };

  const calculateProfileCompleteness = (): number => {
    let completeness = 0;
    const fields = [
      user.firstName,
      user.lastName,
      user.email,
      user.isEmailVerified,
      user.photoURL,
      user.isApproved,
    ];

    fields.forEach(field => {
      if (field) completeness += (100 / fields.length);
    });

    return Math.round(completeness);
  };

  const completeness = calculateProfileCompleteness();
  const isComplete = completeness >= 100;

  // ===== Event Handlers =====
  const handleEditProfile = () => {
    navigate('/settings');
  };

  // ===== Render =====
  return (
    <Card
      sx={{
        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        color: 'white',
        mb: 3,
      }}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
          {/* Left: User Info */}
          <Box display="flex" alignItems="center" gap={2}>
            <Avatar
              src={user.photoURL}
              sx={{
                width: 64,
                height: 64,
                backgroundColor: 'rgba(255,255,255,0.3)',
                fontSize: '1.5rem',
                fontWeight: 'bold',
              }}
            >
              {user.firstName?.[0]}{user.lastName?.[0]}
            </Avatar>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Welcome back, {user.firstName}!
              </Typography>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {user.email}
                </Typography>
                <Chip
                  label={user.role?.toUpperCase()}
                  size="small"
                  color={getRoleColor(user.role || 'viewer')}
                  sx={{ fontWeight: 'bold' }}
                />
                {user.isEmailVerified && (
                  <Chip
                    label="VERIFIED"
                    size="small"
                    sx={{
                      backgroundColor: 'rgba(76, 175, 80, 0.8)',
                      color: 'white',
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          {/* Right: Profile Completeness */}
          {!isComplete && (
            <Box sx={{ minWidth: 200 }}>
              <Typography variant="body2" gutterBottom sx={{ opacity: 0.9 }}>
                Profile Completeness
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <LinearProgress
                  variant="determinate"
                  value={completeness}
                  sx={{
                    flex: 1,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'white',
                    },
                  }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold', minWidth: 40 }}>
                  {completeness}%
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditProfile}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Complete Profile
              </Button>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
